import modalStyles from '../../components/modal/modal.module.scss';
import {useIsNavigatingOrPending} from '../../support/use_is_navigating_or_pending';
import {__, transString} from '../../support/lang/translations';
import React, {useCallback, useState} from 'react';
import {useClickOutside} from '../../support/use_click_outside.ts';
import {FlexRow} from '../utility/flex_row.tsx';
import {Alignment} from '../../enums/alignment.ts';
import {IdentifierSelect} from '../identifier/identifier_select.tsx';
import {Spacer} from '../utility/spacer.tsx';
import {Vehicle} from '../../gql/customer/graphql.ts';
import {VehicleListItem} from './vehicle_list_item.tsx';
import {List} from '../list/list.tsx';
import {AltButton} from '../button/alt_button.tsx';
import {VehicleData} from '../../network/vehicle_data.ts';
import {useAppDispatch, useAppSelector} from '../../store/store.ts';
import {addVehicle} from '../../store/slices/me_slice.ts';
import {handleSuccess} from '../../support/handle_errors_success.ts';

interface OwnProps {
    onCloseModal: () => void;
    vehicles?: (Vehicle | null)[] | null;
}

export function CreateVehicleModal(props: OwnProps) {
    const isLoading = useIsNavigatingOrPending((state) => state.customerInfo.status);
    const ref = React.useRef<HTMLDivElement | null>(null);
    const [foundVehicleData, setFoundVehicleData] = useState<Vehicle | null>(null);
    const [identifierInput, setIdentifierInput] = useState('');
    const {onCloseModal} = props;
    const closeModalCallback = useCallback(() => {
        onCloseModal();
    }, [onCloseModal]);
    useClickOutside(ref, closeModalCallback);
    const customerToken = useAppSelector((state) => state.exchangeToken.token);
    const customerVehicles = useAppSelector((state) => state.customerInfo?.me?.customer?.vehicles) ?? [];
    const [vehicleAlreadyRegistered, setVehicleAlreadyRegistered] = useState(false);
    const dispatch = useAppDispatch();

    if (customerToken === null) {
        return null;
    }

    const getVehicle = async (vehicleInput: string) => {
        if (vehicleInput.replace(new RegExp('-', 'g'), '').length >= 6) {
            const cleanedInput = vehicleInput.replace(new RegExp('-', 'g'), '').toUpperCase();

            const existingVehicle = customerVehicles.some((vehicle) =>
                vehicle?.identifiers?.some(
                    (identifier) =>
                        identifier?.value === vehicleInput.toUpperCase() || identifier?.value === cleanedInput,
                ),
            );
            setVehicleAlreadyRegistered(existingVehicle);

            if (!existingVehicle) {
                setIdentifierInput(cleanedInput);
                const retrievedData = await VehicleData.getVehicleData(vehicleInput);
                setFoundVehicleData(retrievedData.data.vehicle ?? null);
            } else {
                setFoundVehicleData(null);
            }
        } else {
            setFoundVehicleData(null);
            setVehicleAlreadyRegistered(false);
        }
    };

    const addVehicleClick = async () => {
        dispatch(addVehicle({token: customerToken, license_plate: identifierInput}));
        closeModalCallback();
        handleSuccess(transString('vehicles.modals.flash.success'));
    };

    return (
        <div className={modalStyles.modal_backdrop}>
            <div className={modalStyles.modal}>
                <div className={modalStyles.modal_dialog} ref={ref}>
                    <div className={modalStyles.modal_header}>
                        <span />
                        <h2>{__('vehicles.modals.titles.create')}</h2>
                        <button disabled={isLoading} type="button" onClick={() => props.onCloseModal()}>
                            <i className="fa-light fa-close"></i>
                        </button>
                    </div>
                    <div className={modalStyles.modal_body} style={{minHeight: 400}}>
                        <p style={{margin: '0 0 2rem 0'}}>{__('vehicles.modals.explanations.create')}</p>

                        <FlexRow alignment={Alignment.Center}>
                            <IdentifierSelect onChange={getVehicle} />
                        </FlexRow>

                        {vehicleAlreadyRegistered && (
                            <FlexRow alignment={Alignment.Center}>
                                <span className={modalStyles.error}>
                                    {__('vehicles.modals.explanations.already_exists')}
                                </span>
                            </FlexRow>
                        )}

                        {foundVehicleData && (
                            <>
                                <Spacer />
                                <FlexRow alignment={Alignment.Start}>
                                    <h4>{__('vehicles.modals.titles.results')}</h4>
                                </FlexRow>
                                <List>
                                    <VehicleListItem
                                        vehicle={foundVehicleData}
                                        button={
                                            <AltButton type="submit" onClick={addVehicleClick}>
                                                <i className="fa-light fa-add" />
                                            </AltButton>
                                        }
                                    />
                                </List>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
